export const CONSENT_STORAGE_KEY = "vf_cookie_consent";
export const CONSENT_SCHEMA_VERSION = 2;
export const CONSENT_POLICY_VERSION = "2026-08-30";
export const CONSENT_PREVIOUS_POLICY_VERSION = "2026-08-28";
export const CONSENT_MAX_AGE_DAYS = 180;
export const GOOGLE_CONSENT_BOOTSTRAP_ERROR_EVENT =
  "vf:google-consent-bootstrap-error";
export const GOOGLE_CONSENT_BOOTSTRAP_ERROR_CODE =
  "google_consent_bootstrap_missing";

export type ConsentChoice = {
  analytics: boolean;
  marketing: boolean;
};

export type ConsentMethod =
  | "accept_all"
  | "reject_optional"
  | "customize"
  | "gpc"
  | "us_analytics_default"
  | "us_opt_out_default";

export type ConsentRecord = ConsentChoice & {
  version: number;
  policyVersion: string;
  updatedAt: string;
  expiresAt: string;
  method: ConsentMethod;
  gpc: boolean;
};

export type ConsentUpdateResult = ConsentRecord & {
  reloadRequired: boolean;
};

export type GoogleConsentBootstrapError = {
  code: typeof GOOGLE_CONSENT_BOOTSTRAP_ERROR_CODE;
  message: string;
  missing: ("dataLayer" | "gtag" | "consent_default")[];
};

export type ConsentApi = {
  hasChoice: () => boolean;
  read: () => ConsentRecord | null;
  has: (category: keyof ConsentChoice) => boolean;
  isGpcEnabled: () => boolean;
  update: (
    choice: ConsentChoice,
    method?: ConsentMethod
  ) => ConsentUpdateResult;
};

declare global {
  interface Navigator {
    globalPrivacyControl?: boolean;
  }

  interface Window {
    vfConsent?: ConsentApi;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __vfGoogleConsentDefaulted?: boolean;
    __vfGoogleConsentBootstrapError?: GoogleConsentBootstrapError;
    fbq?: ((...args: unknown[]) => void) & Record<string, unknown>;
    _fbq?: unknown;
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

const isBoolean = (value: unknown): value is boolean =>
  typeof value === "boolean";

export const isConsentRecordValid = (
  value: unknown,
  now = Date.now()
): value is ConsentRecord => {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ConsentRecord>;
  const policyVersionSupported =
    record.policyVersion === CONSENT_POLICY_VERSION ||
    record.policyVersion === CONSENT_PREVIOUS_POLICY_VERSION;
  if (
    record.version !== CONSENT_SCHEMA_VERSION ||
    !policyVersionSupported ||
    !isBoolean(record.analytics) ||
    !isBoolean(record.marketing) ||
    !isBoolean(record.gpc) ||
    typeof record.updatedAt !== "string" ||
    typeof record.expiresAt !== "string" ||
    typeof record.method !== "string"
  ) {
    return false;
  }
  const expiresAt = Date.parse(record.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt > now;
};

export const applyPrivacySignals = (
  choice: ConsentChoice,
  gpc: boolean
): ConsentChoice => ({
  analytics: Boolean(choice.analytics),
  // GPC is a legally recognized opt-out signal in several US states. It must
  // override an older advertising preference on this browser/device.
  marketing: gpc ? false : Boolean(choice.marketing),
});

const readStoredRecord = (): ConsentRecord | null => {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isConsentRecordValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const persistRecord = (record: ConsentRecord): void => {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Privacy choices still apply for the current page when storage is blocked.
  }
};

const gpcEnabled = (): boolean =>
  typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;

const metaContent = (name: string): string =>
  document
    .querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
    ?.content.trim() || "";

const googleConsentParams = (choice: ConsentChoice) => ({
  analytics_storage: choice.analytics ? "granted" : "denied",
  ad_storage: choice.marketing ? "granted" : "denied",
  ad_user_data: choice.marketing ? "granted" : "denied",
  ad_personalization: choice.marketing ? "granted" : "denied",
  functionality_storage: "granted",
  security_storage: "granted",
});

type GoogleConsentBootstrap = {
  dataLayer: unknown[];
  gtag: (...args: unknown[]) => void;
};

const reportMissingGoogleConsentBootstrap = (
  missing: GoogleConsentBootstrapError["missing"]
): void => {
  if (window.__vfGoogleConsentBootstrapError) return;

  const diagnostic: GoogleConsentBootstrapError = {
    code: GOOGLE_CONSENT_BOOTSTRAP_ERROR_CODE,
    message: `GTM was not loaded because the synchronous Google consent bootstrap is missing: ${missing.join(
      ", "
    )}.`,
    missing,
  };
  window.__vfGoogleConsentBootstrapError = diagnostic;

  // Analytics failures must remain non-fatal for the storefront, while still
  // being visible to monitoring and browser diagnostics.
  try {
    console.error(`[VueFront privacy] ${diagnostic.message}`);
  } catch {
    // Some hardened browsers replace or block the console object.
  }
  try {
    window.dispatchEvent(
      new CustomEvent<GoogleConsentBootstrapError>(
        GOOGLE_CONSENT_BOOTSTRAP_ERROR_EVENT,
        { detail: diagnostic }
      )
    );
  } catch {
    // A diagnostic listener must never be able to interrupt the storefront.
  }
};

const getGoogleConsentBootstrap = (): GoogleConsentBootstrap | null => {
  // Once the synchronous bootstrap has been missed, keep Google disabled for
  // this page. Loading it later would no longer preserve consent-before-tags.
  if (window.__vfGoogleConsentBootstrapError) return null;

  const missing: GoogleConsentBootstrapError["missing"] = [];
  if (!Array.isArray(window.dataLayer)) missing.push("dataLayer");
  if (typeof window.gtag !== "function") missing.push("gtag");
  if (window.__vfGoogleConsentDefaulted !== true) {
    missing.push("consent_default");
  }
  if (missing.length) {
    reportMissingGoogleConsentBootstrap(missing);
    return null;
  }

  return {
    dataLayer: window.dataLayer as unknown[],
    gtag: window.gtag as (...args: unknown[]) => void,
  };
};

const loadGoogleTagManager = (choice: ConsentChoice): void => {
  const containerId = metaContent("vf:gtm-container");
  if (!/^GTM-[A-Z0-9]+$/.test(containerId)) return;

  const bootstrap = getGoogleConsentBootstrap();
  if (!bootstrap) return;

  bootstrap.gtag("consent", "update", googleConsentParams(choice));
  if (document.querySelector("script[data-vf-gtm]")) return;

  bootstrap.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    containerId
  )}`;
  script.setAttribute("data-vf-gtm", containerId);
  document.head.appendChild(script);
};

const loadMetaPixel = (): void => {
  const pixelId = metaContent("vf:meta-pixel");
  if (!/^\d+$/.test(pixelId)) return;

  if (window.fbq) {
    window.fbq("consent", "grant");
    return;
  }

  const queue: unknown[][] = [];
  const fbq = ((...args: unknown[]) => {
    // Meta replaces callMethod when its runtime arrives. Keep forwarding live
    // events after that point instead of accumulating them in a dead queue.
    if (typeof fbq?.callMethod === "function") fbq.callMethod(...args);
    else queue.push(args);
  }) as Window["fbq"];
  if (!fbq) return;
  fbq.queue = queue;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window._fbq = fbq;
  fbq("init", pixelId);
  fbq("consent", "grant");
  fbq("track", "PageView");

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  script.setAttribute("data-vf-meta", pixelId);
  document.head.appendChild(script);
};

const expireCookie = (name: string): void => {
  const encoded = encodeURIComponent(name);
  const paths = ["/", window.location.pathname || "/"];
  const host = window.location.hostname;
  const domains = ["", host, host ? `.${host.replace(/^www\./, "")}` : ""];
  paths.forEach((path) => {
    domains.forEach((domain) => {
      document.cookie = `${encoded}=; Max-Age=0; Path=${path}; SameSite=Lax${
        domain ? `; Domain=${domain}` : ""
      }`;
    });
  });
};

const clearVendorStorage = (choice: ConsentChoice): void => {
  if (!choice.analytics) {
    try {
      window.localStorage.removeItem("vf_rum_seen");
    } catch {
      /* Optional storage. */
    }
  }
  const analyticsPrefixes = ["_ga", "_gid", "_gat", "_clck", "_clsk"];
  const marketingPrefixes = [
    "_gcl_",
    "_fbp",
    "_fbc",
    "__kla",
    "kla_",
    "_pin_",
    "_pinterest_",
    "_epik",
    "_derived_epik",
  ];
  const prefixes = [
    ...(choice.analytics ? [] : analyticsPrefixes),
    ...(choice.marketing ? [] : marketingPrefixes),
  ];

  document.cookie
    .split(";")
    .map((part) => decodeURIComponent(part.split("=", 1)[0].trim()))
    .filter((name) => prefixes.some((prefix) => name.startsWith(prefix)))
    .forEach(expireCookie);

  if (!choice.marketing) {
    try {
      Object.keys(window.localStorage)
        .filter((key) => /klaviyo|^_?kl_/i.test(key))
        .forEach((key) => window.localStorage.removeItem(key));
      Object.keys(window.sessionStorage)
        .filter((key) => /klaviyo|^_?kl_/i.test(key))
        .forEach((key) => window.sessionStorage.removeItem(key));
    } catch {
      // Storage may be unavailable in hardened privacy modes.
    }
  }
};

const applyVendors = (choice: ConsentChoice): void => {
  // GTM must also load for a fully denied choice so Consent Mode can receive
  // and expose the denied state. Individual tags remain consent-gated in GTM.
  loadGoogleTagManager(choice);
  if (choice.marketing) {
    loadMetaPixel();
  } else {
    window.fbq?.("consent", "revoke");
  }
  clearVendorStorage(choice);
};

const dispatchUpdate = (record: ConsentRecord): void => {
  window.dispatchEvent(
    new CustomEvent<ConsentRecord>("vf:consent-updated", { detail: record })
  );
};

export const installConsentManager = (): ConsentApi | null => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }
  if (window.vfConsent) return window.vfConsent;

  const read = (): ConsentRecord | null => {
    const stored = readStoredRecord();
    if (!stored) return null;

    const gpc = gpcEnabled();
    if (stored.method === "us_analytics_default") {
      // Migrate only the former automatic default. Explicit Reject/Customize
      // choices are preserved exactly as the visitor set them.
      const migrated = createRecord(
        { analytics: true, marketing: true },
        gpc ? "gpc" : "us_opt_out_default",
        gpc
      );
      persistRecord(migrated);
      return migrated;
    }
    if (gpc && stored.marketing) {
      const forced = createRecord(
        { analytics: stored.analytics, marketing: false },
        "gpc",
        true
      );
      persistRecord(forced);
      return forced;
    }
    if (stored.policyVersion !== CONSENT_POLICY_VERSION) {
      // Carry explicit choices forward when the disclosure text changes. A
      // policy-version migration must never turn a previous opt-out back on.
      const migrated = createRecord(
        { analytics: stored.analytics, marketing: stored.marketing },
        stored.method,
        gpc
      );
      persistRecord(migrated);
      return migrated;
    }
    return stored;
  };

  const api: ConsentApi = {
    hasChoice: () => Boolean(read()),
    read,
    has: (category) => Boolean(read()?.[category]),
    isGpcEnabled: gpcEnabled,
    update: (choice, method = "customize") => {
      const previous = read();
      const gpc = gpcEnabled();
      const record = createRecord(choice, gpc ? "gpc" : method, gpc);
      const reloadRequired = Boolean(
        (previous?.analytics && !record.analytics) ||
          (previous?.marketing && !record.marketing)
      );
      persistRecord(record);
      applyVendors(record);
      dispatchUpdate(record);
      return { ...record, reloadRequired };
    },
  };

  window.vfConsent = api;
  const initial = read();
  if (initial) {
    applyVendors(initial);
  } else if (metaContent("vf:consent-experience") === "us-opt-out-default") {
    // The US storefront uses an opt-out experience for both optional
    // categories. GPC continues to override advertising on every visit.
    const defaultRecord = createRecord(
      { analytics: true, marketing: true },
      "us_opt_out_default",
      gpcEnabled()
    );
    persistRecord(defaultRecord);
    applyVendors(defaultRecord);
    dispatchUpdate(defaultRecord);
  } else {
    applyVendors({ analytics: false, marketing: false });
  }
  return api;
};

export const createRecord = (
  choice: ConsentChoice,
  method: ConsentMethod,
  gpc: boolean,
  now = Date.now()
): ConsentRecord => {
  const normalized = applyPrivacySignals(choice, gpc);
  return {
    version: CONSENT_SCHEMA_VERSION,
    policyVersion: CONSENT_POLICY_VERSION,
    ...normalized,
    updatedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + CONSENT_MAX_AGE_DAYS * DAY_MS).toISOString(),
    method,
    gpc,
  };
};
